import { encodeLayout, type LayoutOp } from './layout';
import { VID, CHUNK_SIZE, PAPER_PRESETS, DOTS_PER_CHAR, LINE_SPACING_DOTS, DOTS_PER_MM, mm } from './constants';
import * as C from './commands';
import type { PaperType, QrMethod } from './commands';
import type { DeviceIdentity, PrinterProfile } from './profile';
import { RULER_STEP_DOTS, rulerLabels, widthProbeLine, edgeProbeLines, type TraceSample } from './calibration';

/** One line of the wire log: every byte that crossed the cable, in either direction. */
export interface WireEntry { t: number; dir: 'tx' | 'rx' | 'note'; hex: string }

/** The minimum of `navigator.usb` this driver uses — so a simulator can stand in for it. */
export interface UsbProvider {
  requestDevice(o: { filters: unknown[] }): Promise<USBDevice>;
  getDevices(): Promise<USBDevice[]>;
  addEventListener?(type: 'disconnect', l: EventListener): void;
  removeEventListener?(type: 'disconnect', l: EventListener): void;
}

/**
 * THE HEADER SLOT. With "header" off the stock carries its own pre-printed header, and the ticket
 * leaves the space the text header would have used: 5 lines of header + 1 line + the separator.
 * Pictures leave the same space, so both kinds of ticket clear the stock's artwork the same way.
 * ✅ FIELD (EP802, Zimbabwe stock): print starts ~29 mm into the ticket (the blade is 29 mm past the head),
 * + 21 mm = ~50 mm, below the stock's rule at 44 mm.
 */
const HEADER_SLOT_LINES = 6;
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export type PaperSize = '58' | '80' | 'off';

/** `0519:2013` — logged on every connect, so a unit that reports something unexpected is a
 *  fact in the log rather than a device that mysteriously never appears in the chooser. */
export function hexId(d: { vendorId?: number; productId?: number }): string {
  const h = (n?: number) => (n ?? 0).toString(16).padStart(4, '0');
  return `${h(d.vendorId)}:${h(d.productId)}`;
}

/**
 * EVERY VALUE THAT REACHES THE PAPER, COERCED BEFORE IT IS UPPER-CASED.
 *
 * `(t.seat || '-').toUpperCase()` threw "toUpperCase is not a function" at a live counter and took
 * the rest of the ticket with it: the Seat and Entry LABELS printed, the values did not, and the
 * separator, QR code, barcode and summary strip after them never ran. An operator was handed a
 * ticket with no QR on it — unscannable at the gate — and nothing said why.
 *
 * `Seat.seatNumber` is an INTEGER (confirmed on production: pg_typeof -> integer). The
 * MasungTicketData interface types these fields as `string`, so TypeScript saw nothing; the values
 * come off a JSON payload at runtime and a number arrives as a number. Any field here can do the
 * same — a row named "12", a section numbered rather than named.
 *
 * So coercion happens in ONE place, and the printer stops assuming its input's type. A device
 * driver should be the last thing in the stack that crashes on a number.
 */
const up = (v: unknown, fallback = '-'): string => {
  if (v === null || v === undefined || v === '') return fallback;
  return String(v).toUpperCase();
};

/** The opening bytes of a rejected chunk, so the log names the command that stalled the pipe
 *  instead of just saying a write failed. ESC/POS is prefix-addressed, so the first few bytes
 *  identify it. */
function hexPreview(chunk: Uint8Array, n = 8): string {
  return Array.from(chunk.slice(0, n))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(' ') + (chunk.length > n ? ' …' : '');
}

export interface MasungPrinterStatus {
  connected: boolean;
  deviceName?: string;
  error?: string;
}

/**
 * `Printable` rather than `string`, because the values do not arrive as strings.
 *
 * These are read straight off an order's JSON, where `seat.seatNumber` is an integer and a row or
 * section can be named with digits. Typing them `string` did not make them strings — it only hid
 * the fact from the compiler, until `.toUpperCase()` threw mid-ticket at a counter. The type now
 * says what actually turns up, and `up()` is what makes it printable.
 */
type Printable = string | number | null | undefined;

export interface MasungTicketData {
  eventName: Printable;
  subtitle: Printable;
  attendeeName: Printable;
  ticketType: Printable;
  gate: Printable;
  section: Printable;
  row: Printable;
  seat: Printable;
  date: Printable;
  time: Printable;
  entryWindow: Printable;
  venue: Printable;
  orderId: Printable;
  qrData: string;
  /** The game's real teams, when the event has a fixture — the title is not parsed for them. */
  homeTeam?: Printable;
  awayTeam?: Printable;
  mode?: 'cut' | 'tear';    // default: tear (black mark)
  showHeader?: boolean;      // default: true
}

export class MasungPrinter {
  device: USBDevice | null = null;
  private _paper: PaperSize = '58';
  private _outEp = 0;
  private _inEp = 0;
  /** Which interface we actually claimed, so `disconnect` releases that one and not a guess. */
  private _ifaceNumber = 0;
  /**
   * ONE CONNECT AT A TIME, ACROSS EVERY CALLER.
   *
   * This class is a module singleton, and the till mounts TWO `useMasungPrinter()` instances at
   * once — the page and the PrintStation inside it — each of which auto-connects on mount. Both
   * raced to `open()` and `claimInterface()` on the same device, and the loser threw "Failed to
   * execute 'claimInterface' on 'USBDevice'", which is the error reported from the counter. A
   * manual Connect click landing while an auto-connect was still in flight did the same thing.
   *
   * Callers share the in-flight attempt instead of starting a second one, so the number of
   * components asking stops being able to change the outcome.
   */
  private _connecting: Promise<void> | null = null;
  log: string[] = [];
  /**
   * EVERY BYTE, BOTH WAYS. No reply from this printer had ever been recorded — the parsers were
   * written from vendor code and never checked against a real answer. The setup screen shows this
   * log live, so "the printer said 0x12" is something an operator can see and send to support.
   */
  wire: WireEntry[] = [];
  private noteWire(dir: WireEntry['dir'], bytes: Uint8Array | string) {
    const hex = typeof bytes === 'string' ? bytes : hexPreview(bytes, 48);
    this.wire = [...this.wire.slice(-399), { t: Date.now(), dir, hex }];
  }

  private _usb: UsbProvider | null = null;
  /** Swap in a simulated bus (dev/test). Defaults to the browser's WebUSB. */
  useUsb(provider: UsbProvider | null) { this._usb = provider; }
  get usb(): UsbProvider { return this._usb ?? (navigator.usb as unknown as UsbProvider); }
  get isSimulated() { return !!this._usb; }

  /** Class and protocol of the interface we claimed — 7/2 is a bidirectional USB printer. */
  private _ifaceClass?: number;
  private _ifaceProtocol?: number;

  /** What the device says it is, straight from its USB descriptors. */
  identity(): DeviceIdentity | null {
    const d = this.device;
    if (!d) return null;
    return {
      vendorId: d.vendorId, productId: d.productId,
      productName: d.productName ?? undefined,
      manufacturerName: (d as any).manufacturerName ?? undefined,
      serialNumber: (d as any).serialNumber ?? undefined,
      interfaceClass: this._ifaceClass, interfaceProtocol: this._ifaceProtocol,
    };
  }
  private _onDisconnect: EventListener | null = null;
  private _statusCallbacks: ((status: MasungPrinterStatus) => void)[] = [];

  get connected() { return !!this.device; }
  get paper() { return this._paper; }

  // ── Status callback system ──
  onStatusChange(cb: (status: MasungPrinterStatus) => void) {
    this._statusCallbacks.push(cb);
  }
  removeStatusChangeListener(cb: (status: MasungPrinterStatus) => void) {
    const idx = this._statusCallbacks.indexOf(cb);
    if (idx > -1) this._statusCallbacks.splice(idx, 1);
  }
  private _notifyStatus(status: MasungPrinterStatus) {
    this._statusCallbacks.forEach(cb => cb(status));
  }

  private addLog(msg: string) {
    this.log = [...this.log.slice(-199), `[${new Date().toLocaleTimeString()}] ${msg}`];
  }

  /**
   * CLAIM THE INTERFACE THAT ACTUALLY CARRIES THE PRINT CHANNEL, not interface 0 by assumption.
   *
   * This claimed `0` and then read endpoints from `interfaces[0]`. Interface 0 is the printer
   * channel on the common units, but it is a convention, not a rule: a composite device (one that
   * also exposes a card reader, a display or a serial bridge) can put the bulk endpoints on a later
   * interface, and then claiming 0 either fails outright or succeeds on the wrong one and prints
   * nothing. Picking the interface by what it CAN DO removes the guess.
   */
  private async claimPrinterInterface() {
    const d = this.device!;
    const candidates = d.configuration?.interfaces ?? [];
    for (const iface of candidates) {
      const eps = iface.alternate?.endpoints ?? [];
      const out = eps.find((e) => e.type === 'bulk' && e.direction === 'out');
      const inn = eps.find((e) => e.type === 'bulk' && e.direction === 'in');
      if (!out) continue;
      await d.claimInterface(iface.interfaceNumber);
      this._ifaceClass = (iface.alternate as any)?.interfaceClass;
      this._ifaceProtocol = (iface.alternate as any)?.interfaceProtocol;
      this._ifaceNumber = iface.interfaceNumber;
      this._outEp = out.endpointNumber;
      this._inEp = inn?.endpointNumber ?? 0;
      this.addLog(`Claimed interface ${iface.interfaceNumber} (EP_OUT=${this._outEp}, EP_IN=${this._inEp})`);
      return;
    }
    // A receipt printer exposes a bulk OUT endpoint to stream ESC/POS bytes into; a device without
    // one cannot be driven, whatever its ID says. This is where a wrong pick gets rejected.
    const name = d.productName || 'That device';
    throw new Error(`${name} isn't a receipt printer — it has no channel to send print data to.`);
  }

  /**
   * WHY A CLAIM FAILS, in the words of the three things that actually cause it.
   *
   * The browser says "Failed to execute 'claimInterface' on 'USBDevice'", which tells an operator at
   * a counter nothing they can act on. In practice it is nearly always one of these, and the first
   * was ours: two `useMasungPrinter()` instances mount together on the till (the page itself and
   * PrintStation inside it), so two auto-connects raced for the same interface on the same shared
   * device and the loser threw this.
   */
  private claimFailureMessage(e: any): string {
    const raw = e?.message ?? String(e);
    return (
      `Could not take control of the printer. ` +
      `Usually one of: it is already open in another tab or window; ` +
      `the operating system has it installed as a normal printer and is holding it ` +
      `(remove it from the system's printer list and try again); ` +
      `or the cable was replugged and the page needs a reload. (${raw})`
    );
  }

  async connect(paper: PaperSize = '58') {
    /**
     * NO FILTER, ON PURPOSE.
     *
     * A WebUSB filter grants nothing and verifies nothing. Its only effect is to choose which
     * devices the operator SEES in the chooser, so it can only ever subtract — and when it
     * subtracts the printer, the chooser comes up empty and the browser reports that as
     * "No device selected", which describes a choice the operator was never offered.
     *
     * This used to filter `{ vendorId: 0x0519, productId: 0x2013 }`. That is one model of one
     * manufacturer at one firmware revision. It silently excludes the rest of the EP series,
     * a re-flashed unit, a unit in a different interface mode, and — the common case for cheap
     * thermal printers — any unit that enumerates through a USB-serial bridge, whose vendor ID
     * belongs to the bridge chip maker rather than to Masung.
     *
     * There is nothing to disambiguate anyway. The real-world state is one till, one printer,
     * and a human looking straight at it; Chrome already hides hubs and protected-class
     * devices. So: show everything, let the person pick the thing they can see, and do the
     * checking AFTERWARDS, where a rejection can say what was wrong (`claimPrinterInterface`).
     *
     * Filtering is silent exclusion. Checking is explainable rejection. Same information,
     * opposite failure mode.
     */
    if (this.device) {
      // Already have one. Re-running open/claim on a live handle is what throws, and the operator
      // pressing Connect on a working printer means "make sure", not "start again".
      this.addLog('Connect ignored — already connected');
      return;
    }
    const picked = await this.usb.requestDevice({ filters: [] });
    this.addLog(`Selected: ${picked.productName || 'MASUNG'} (${hexId(picked)})`);
    await this.bringUp(picked, paper, 'Connected');
  }

  async autoConnect(paper: PaperSize = '58'): Promise<boolean> {
    if (!this._usb && !navigator.usb) return false;
    if (this.device) return true; // a sibling component already brought it up
    // `getDevices()` returns only what this operator already granted THIS origin — i.e. the
    // printer they picked at the start of a previous shift. Prefer a Masung if one is in that
    // set; otherwise, if they only ever granted one device, that device IS the printer. The
    // old `vendorId && productId === 0x2013` match meant a till with any other model had to be
    // re-picked by hand every single shift.
    const devices = await this.usb.getDevices();
    const match = devices.find(d => d.vendorId === VID) ?? (devices.length === 1 ? devices[0] : undefined);
    if (!match) return false;
    this.addLog(`Auto-connect: ${match.productName || 'MASUNG'} (${hexId(match)})`);
    await this.bringUp(match, paper, 'Auto-connected');
    return true;
  }

  /**
   * Open, claim, configure — the sequence both entry points share, run at most once at a time.
   *
   * It was duplicated in `connect` and `autoConnect`, which is how the two drifted into racing:
   * each opened and claimed independently on the same singleton. Single-flight now, so a second
   * caller arriving mid-connect waits for the first rather than claiming an interface that is
   * already being claimed.
   */
  private async bringUp(device: USBDevice, paper: PaperSize, label: string): Promise<void> {
    if (this._connecting) return this._connecting;

    this._connecting = (async () => {
      try {
        this.device = device;
        // A read left pending on the previous handle belongs to a device that no longer exists.
        this._pendingIn = null;
        // A fresh connection knows nothing about where the roll is sitting — the operator may have
        // replugged, reloaded, or torn a ticket off by hand. The first ticket re-registers.
        this._registeredAtMark = false;
        await device.open();
        if (!device.configuration) await device.selectConfiguration(1);
        await this.claimPrinterInterface();
        // ✅ FIELD: the printer keeps unread replies across page reloads, so the first status after a
        // reconnect read 0x60 — an old answer, not the state. Drain before asking anything.
        let stale = 0;
        for (let i = 0; i < 4; i++) { const b = await this.read(64, 60); if (!b.length) break; stale += b.length; }
        if (stale) this.noteWire('note', `discarded ${stale} stale byte(s) left in the printer from an earlier session`);
        this._paper = paper;
        await this.write(C.CMD_INIT);
        await this.write(C.cmdSetCommandMode(3));  // EPOS mode — enables mark sensor
        const area = this.printAreaCommand();
        if (area) await this.write(area);
        this.addLog(`${label} (paper=${paper}mm, EP_OUT=${this._outEp}, EP_IN=${this._inEp})`);
        this.noteWire('note', `connected ${hexId(device)} "${device.productName ?? ''}" iface class ${this._ifaceClass ?? '?'}/${this._ifaceProtocol ?? '?'}`);
        this.listenForDisconnect();
        this._notifyStatus({ connected: true, deviceName: device.productName || 'USB printer' });
      } catch (e: any) {
        // Leave nothing half-open behind: a device we opened but could not claim stays open at the
        // OS level and blocks the next attempt, which turns one bad connect into a permanent one.
        try { await device.close(); } catch {}
        this.device = null;
        const msg = this.claimFailureMessage(e);
        this.addLog(msg);
        this._notifyStatus({ connected: false, error: msg });
        throw new Error(msg);
      } finally {
        this._connecting = null;
      }
    })();

    return this._connecting;
  }

  private listenForDisconnect() {
    const usb = this.usb as any;
    if (this._onDisconnect) usb.removeEventListener?.('disconnect', this._onDisconnect);
    this._onDisconnect = ((e: any) => {
      if (e?.device === this.device) {
        this.device = null;
        this._registeredAtMark = false;
        if (this._expectingReset) {
          // Not a fault — we asked for it. See `writeSetting`.
          this.addLog('Printer restarting to store the setting…');
          this._notifyStatus({ connected: false, error: 'Printer is restarting to save that setting…' });
          void this.waitForReturn();
          return;
        }
        this.addLog('Device disconnected');
        this._notifyStatus({ connected: false, error: 'Device disconnected' });
      }
    }) as EventListener;
    usb.addEventListener?.('disconnect', this._onDisconnect);
  }

  /** True while a parameter write is expected to restart the printer — see `writeSetting`. */
  private _expectingReset = false;

  /**
   * A SETTING THAT RESTARTS THE PRINTER, HANDLED AS SUCH.
   *
   * `0x13 0x74 …` is Masung's vendor parameter-write family, and the cut and print offsets are in
   * it. Those go to the printer's NVRAM, and storing them RESTARTS the unit to apply — so it drops
   * off USB and comes back a second or two later as a fresh device. Nothing about that is a fault,
   * and it cannot be prevented: it is how the firmware persists a setting.
   *
   * Every attempt to fix this in the transport layer failed for that reason — clearing a stalled
   * endpoint on a device that has ceased to exist does nothing. The reported remedy was unplugging
   * the cable, which worked only because it forced a fresh enumeration that the page then picked up.
   *
   * WHAT MAKES IT RECOVERABLE: WebUSB permission survives re-enumeration. `getDevices()` still
   * returns the printer afterwards, so re-opening it needs no chooser, no click and no replug. The
   * offset is applied, the unit restarts, we wait for it and take it back.
   */
  private async writeSetting(command: Uint8Array, label: string) {
    // A stored offset is exactly the kind of change whose first ticket lands wrong — and this one
    // restarts the printer as well, so it has more to settle than the rest.
    this._settingsDirty = true;
    this._expectingReset = true;
    this.addLog(`${label} — the printer may restart to store this`);
    try {
      await this.write(command);
    } catch (e: any) {
      // A device that resets mid-write tears the transfer down; that is the restart, not a failure
      // to send. The command reached it, which is why it is restarting.
      this.addLog(`${label}: transfer ended as the printer restarted (${e?.message ?? e})`);
    }
    // Some units drop immediately, others finish the transfer first and go a moment later.
    await this.waitForReturn();
  }

  /**
   * Wait for the printer to come back after a restart, then re-open it.
   *
   * Bounded: if it has not returned in ~8 seconds it is not coming back on its own, and saying so
   * is better than a spinner that never resolves.
   */
  /** Both the disconnect listener and `writeSetting` wait for the restart; they share one wait. */
  private _returning: Promise<void> | null = null;
  private waitForReturn(): Promise<void> {
    if (!this._returning) this._returning = this.waitForReturnOnce().finally(() => { this._returning = null; });
    return this._returning;
  }

  private async waitForReturnOnce() {
    const paper = this._paper;
    /**
     * FIRST SEE IT GO. Polling straight away found the OLD handle still enumerated — the unit had not
     * restarted yet — and declared "back" before it had left; the real restart then landed as an
     * unexplained disconnect. So wait for the drop (the disconnect listener clears `device`). If it
     * never drops, this firmware applied the setting live, and that is recorded as what happened.
     */
    for (let waited = 0; this.device && waited < 3000; waited += 100) await sleep(100);
    if (this.device) {
      this._expectingReset = false;
      this.addLog('No restart observed — the printer applied the setting without dropping off USB');
      this.noteWire('note', 'setting applied without a restart');
      return;
    }
    this.noteWire('note', 'printer dropped off USB to store the setting — waiting for it to return');
    for (let attempt = 0; attempt < 16; attempt++) {
      await new Promise((r) => setTimeout(r, 500));
      try {
        const devices = await this.usb.getDevices();
        const match = devices.find((d) => d.vendorId === VID) ?? (devices.length === 1 ? devices[0] : undefined);
        if (!match) continue;
        this._connecting = null;
        await this.bringUp(match, paper, 'Reconnected after restart');
        this._expectingReset = false;
        this.addLog('Setting stored; printer is back');
        return;
      } catch {
        // Still restarting — its interface is not claimable yet. Try again.
      }
    }
    this._expectingReset = false;
    const msg = 'The printer restarted to save that setting but has not come back. Unplug it and plug it in again.';
    this.addLog(msg);
    this._notifyStatus({ connected: false, error: msg });
  }

  async disconnect() {
    if (!this.device) return;
    // The interface we actually claimed, not 0 by assumption — releasing the wrong one leaves the
    // real one held, and the next connect fails to claim it.
    try { await this.device.releaseInterface(this._ifaceNumber); } catch {}
    try { await this.device.close(); } catch {}
    this.device = null;
    this.addLog('Disconnected');
    this._notifyStatus({ connected: false });
  }

  /**
   * A STALLED ENDPOINT IS WHY THE PRINTER "NEEDS UNPLUGGING".
   *
   * `transferOut` can come back `stall` — the device rejected the packet — and a stalled endpoint
   * STAYS halted until somebody calls `clearHalt`. This threw on the stall and never cleared it, so
   * from that moment every write failed and the only way back was pulling the cable, which
   * re-enumerates the device and resets the endpoint. Reported from the counter as the printer
   * disconnecting when settings are changed: a vendor parameter command the firmware rejects (the
   * `0x13 0x74 …` mark-offset pair are the ones a settings change sends) stalls the pipe, and
   * everything afterwards is dead for a reason nothing on screen mentions.
   *
   * Clearing the halt is the documented recovery and costs one round trip, so it is tried before
   * giving up. One retry, not a loop: if the device stalls the same packet twice it is refusing the
   * command, and hammering it turns a bad command into a hung till.
   */
  private async clearStall(direction: 'in' | 'out', endpoint: number) {
    try {
      await (this.device as any)?.clearHalt(direction, endpoint);
      this.addLog(`Cleared ${direction.toUpperCase()} endpoint stall (ep=${endpoint})`);
      return true;
    } catch (e: any) {
      this.addLog(`clearHalt failed on ${direction} ep=${endpoint}: ${e?.message ?? e}`);
      return false;
    }
  }

  async write(data: Uint8Array) {
    if (!this.device) throw new Error('Not connected');
    let off = 0;
    while (off < data.length) {
      const chunk = data.slice(off, off + CHUNK_SIZE);
      this.noteWire('tx', chunk);
      let r = await this.device.transferOut(this._outEp, chunk);

      if (r.status !== 'ok') {
        this.addLog(`Write ${r.status} at byte ${off} — first bytes: ${hexPreview(chunk)}`);
        /**
         * A RECOVERED STALL IS NOT A SUCCESS, and treating it as one is how a ticket comes out
         * blank while the screen says it printed.
         *
         * Clearing the halt gets the pipe working again, and the retry can report `ok` — but the
         * printer stalled because it REJECTED those bytes, and it has almost certainly discarded
         * whatever else was in flight with them. The job continues so the paper is cut and the
         * roll ends up somewhere known, and then the caller is told the ticket is suspect. Silence
         * here is the difference between "nothing came out" and "nothing came out AND the app said
         * it did".
         */
        this._stalledDuringJob = true;
        if (await this.clearStall('out', this._outEp)) {
          r = await this.device.transferOut(this._outEp, chunk);
        }
      }

      if (r.status !== 'ok') {
        // SAY SO. This used to throw into a caller that mostly swallowed it, leaving the status bar
        // reading "connected" over a printer that could not accept another byte — the operator's
        // only signal was paper that stopped coming out.
        const msg = `Printer rejected a command (${r.status}). The last setting was not applied.`;
        this._notifyStatus({ connected: true, error: msg });
        throw new Error(msg);
      }
      off += chunk.length;
    }
    return data.length;
  }

  /**
   * A READ THAT ENDS. `transferIn` has no timeout in WebUSB; the `timeout` argument this used to take
   * was silently ignored, so a printer that never answers hung every status check for ever.
   *
   * The transfer cannot be cancelled either, so a timed-out one stays queued and will deliver the
   * NEXT bytes the printer sends. It is kept and reused rather than stacked, and `query` drains it
   * before asking anything new, so a late answer to one question is never read as the answer to the
   * next.
   */
  private _pendingIn: Promise<USBInTransferResult> | null = null;

  async read(len = 64, timeout = 1500): Promise<Uint8Array> {
    if (!this.device) throw new Error('Not connected');
    if (!this._inEp) return new Uint8Array(0);
    // Cleared by whoever CONSUMES the result, never on resolution: a reply that lands between a
    // timed-out read and the next one must still be there for the next one to take.
    let p = this._pendingIn;
    if (!p) {
      p = this._pendingIn = this.device.transferIn(this._inEp, Math.max(len, 64));
      p.catch(() => {}); // an abandoned read on an unplugged device must not surface as unhandled
    }
    try {
      const r = await Promise.race([p, sleep(timeout).then(() => null)]);
      if (!r) return new Uint8Array(0);
      if (this._pendingIn === p) this._pendingIn = null;
      if (r.status === 'ok' && r.data) {
        const bytes = new Uint8Array(r.data.buffer, r.data.byteOffset, r.data.byteLength);
        if (bytes.length) this.noteWire('rx', bytes);
        return bytes;
      }
      // Same trap on the way in: a stalled IN endpoint silently returned "no status" for ever, so
      // every status query answered "nothing wrong" from a pipe that was broken.
      if (r.status === 'stall') await this.clearStall('in', this._inEp);
    } catch (e: any) {
      if (this._pendingIn === p) this._pendingIn = null;
      this.addLog(`Read failed: ${e?.message ?? e}`);
    }
    return new Uint8Array(0);
  }

  /**
   * ONE CONVERSATION AT A TIME. A question and its answer are two transfers; if a second caller asks
   * in between, each reads the other's reply. React's dev double-mount did exactly that and every
   * identity answer landed one row down. Multi-step probes take the lock once and use `ask`.
   */
  private _lock: Promise<unknown> = Promise.resolve();
  async exclusive<T>(fn: () => Promise<T>): Promise<T> {
    const run = this._lock.then(fn, fn);
    this._lock = run.catch(() => {});
    return run;
  }

  /** Ask one question and wait for its answer (serialized). */
  async query(cmd: Uint8Array, len = 1, timeout = 800): Promise<Uint8Array> {
    return this.exclusive(() => this.ask(cmd, len, timeout));
  }

  /** `query` without the lock — only for callers already inside `exclusive`. */
  private async ask(cmd: Uint8Array, len = 1, timeout = 800): Promise<Uint8Array> {
    if (this._pendingIn) {
      const stale = await this.read(64, 60);
      if (stale.length) this.noteWire('note', 'discarded a late reply to an earlier question');
    }
    await this.write(cmd);
    const out: number[] = [];
    let deadline = Date.now() + timeout;
    while (out.length < len && Date.now() < deadline) {
      const got = await this.read(64, Math.max(1, deadline - Date.now()));
      if (!got.length) break;
      out.push(...got);
      // A string answer arrives in one burst. Once something has come, wait only briefly for the
      // rest instead of the whole timeout — a short reply is the normal case, not a missing one.
      deadline = Math.min(deadline, Date.now() + 80);
    }
    return new Uint8Array(out);
  }

  async send(...parts: Uint8Array[]) { return this.write(C.concat(...parts)); }
  /**
   * ANYTHING THAT MOVES PAPER OUTSIDE A TICKET INVALIDATES THE REGISTRATION.
   *
   * `_registeredAtMark` is an assumption about where the roll is sitting, and an assumption held
   * after somebody pressed Feed or ran a mark test is just a wrong belief — the next ticket would
   * skip its registration and print off-position, which is the bug this whole branch exists to fix.
   * Every operator-facing paper mover clears it, so the next ticket re-registers.
   */
  private paperMoved() { this._registeredAtMark = false; }

  async feed(n: number) { this.paperMoved(); this.countSlots(n); await this.write(C.cmdFeedLines(n)); }

  /**
   * Feed an arbitrary distance, not just what fits in one ESC J byte.
   *
   * See `cmdFeedDots`: one command tops out at 255 dots (31.9mm). A ticket pitch is three or
   * four times that, so a single command can never pad a ticket to its pitch. Splitting here —
   * rather than letting the command silently truncate — is what makes `finishTicket` able to
   * mean what it says.
   */
  async feedDots(n: number) {
    let left = Math.max(0, Math.round(n));
    while (left > 0) {
      const step = Math.min(left, C.MAX_FEED_DOTS_PER_CMD);
      await this.write(C.cmdFeedDots(step));
      left -= step;
    }
  }
  async cut(full = true) { this.paperMoved(); await this.write(C.cmdCut(full)); }

  /**
   * THE TICKET STOCK HAS NO BLACK MARKS, so nothing re-registers the paper. Verified on the
   * hardware: `GS FF` (detect mark, feed to cut position) moved no paper and set no black-mark
   * error — the printer is not in black-mark mode, and the SDK has no command to put it there.
   * The vendor manual gates all four mark commands on "黑标模式下 / On black mode".
   *
   * With no mark, the ONLY thing that keeps a cut on a perforation is this: every ticket must
   * advance the paper by exactly one pitch. Then cut N lands on perforation N and there is
   * nothing to accumulate. A trailing feed tuned for one ticket cannot do that while the
   * CONTENT height varies — which is what was happening: four optional blocks, up to seven
   * lines (~24.5mm), so a general-admission ticket finished a centimetre short of a seated one
   * and both got the same 240-dot tail.
   *
   * So `printTicket` emits a FIXED number of line slots and this counts them. The count is not
   * cosmetic: it is the invariant the alignment rests on, and a future edit that makes a row
   * conditional again would silently break the cut. Here it says so instead.
   */
  private _slots = 0;
  private _lastTicketSlots: number | null = null;

  /**
   * SIDE MARGINS, so the ticket body is not flush against the paper edge.
   *
   * The detail rows are LEFT-aligned on purpose — they are a two-column table held together by a
   * tab stop, and centring each line would let `Date`/`Kickoff` drift out of step with
   * `24 SEPT 2026`/`3:00 PM`. But left-aligned was being read as "hard against the edge", and on
   * pre-printed stock that runs the text into the printed border.
   *
   * Insetting the whole PRINT AREA fixes that without touching the column alignment: the left
   * margin moves the origin, the width shrinks by the same amount on both sides, and everything
   * inside — tabbed rows and centred banners alike — moves with it. The columns stay exactly as
   * they were relative to each other.
   *
   * Adjustable rather than guessed. The right number depends on the stock's own printed border,
   * which is a physical property of paper I cannot measure from here — so it is tuned at the till
   * like the cut offset was, and remembered.
   */
  private _marginDots = 0;

  /**
   * Characters that fit across the inset print area, and the tab column at its midpoint.
   *
   * The layout used a constant — 32 chars at 58mm, 48 at 80mm, tab at half — which stops being true
   * the moment the area narrows. A tab stop past the right edge does not wrap politely; the second
   * column simply falls off the ticket. So both follow the margin.
   *
   * See DOTS_PER_CHAR — 384 dots across 32 columns on 58mm stock.
   */
  private layoutColumns() {
    const preset = this._paperSpan ? { width: this._paperSpan.widthDots } : this._headDots ? { width: this._headDots } : PAPER_PRESETS[this._paper];
    const width = preset ? preset.width - Math.min(this._marginDots, Math.floor(preset.width / 4)) * 2 : 384;
    const chars = Math.max(16, Math.floor(width / DOTS_PER_CHAR));
    return { chars, tabCol: Math.floor(chars / 2) };
  }

  /**
   * TOP INSET — blank paper fed before the first line of a ticket.
   *
   * Pre-printed stock often carries branding across the top of each ticket, and printing starts at
   * the top of the ticket by design (that is what `registerAtMark` is for), so without this the
   * first lines land on the artwork. Same idea as the side margins, on the other axis.
   *
   * IT COMES OUT OF THE TAIL ON MARKLESS STOCK. There the cut is landed by making every ticket the
   * same total height — top + content + tail — so a top inset added without reducing the tail makes
   * every ticket taller than its pitch and the cut walks further off the perforation with each one.
   * `finishTicket` subtracts it. On marked stock the sensor lands the cut, so nothing needs
   * compensating and the tail is not used at all.
   */
  private _topDots = 0;

  /** Dots of blank paper before the first printed line (8 dots = 1mm). */
  setTopMargin(dots: number) {
    this._settingsDirty = true;
    this._topDots = Math.max(0, Math.round(dots));
    this.addLog(`Top margin set to ${this._topDots} dots (${mm(this._topDots).toFixed(1)}mm)`);
  }
  get topDots() { return this._topDots; }

  /**
   * Feed without disturbing `_registeredAtMark`.
   *
   * The public `feedDots` clears that flag, and rightly so — an operator pressing Feed has moved
   * the paper out from under our assumption. But a feed we issue ourselves INSIDE a ticket is part
   * of the layout, not a loss of position, and clearing the flag there would be a lie about where
   * the roll is.
   */
  private async feedDotsInternal(n: number) {
    let left = Math.max(0, Math.round(n));
    while (left > 0) {
      const step = Math.min(left, C.MAX_FEED_DOTS_PER_CMD);
      await this.write(C.cmdFeedDots(step));
      left -= step;
    }
  }

  /**
   * A SETTING CHANGED, SO THE NEXT TICKET OUT IS THE ONE THAT PROVES IT.
   *
   * The first ticket after an alignment change lands badly and the rest come out right — the
   * printer settles after a restart or a re-registration, and whichever ticket goes first absorbs
   * it. On a four-ticket order that meant binning all four and printing again to get four good
   * ones, because the bad one was a real ticket with a real QR on it.
   *
   * So the next job leads with a throwaway. The operator tears off one marked scrap instead of
   * reprinting a customer's whole order, and the flag clears itself after one use: this is a cost
   * paid once per adjustment, not on every sale.
   */
  private _settingsDirty = false;

  /** True when a layout or paper-handling setting has changed since the last ticket printed. */
  get needsAlignment() { return this._settingsDirty; }

  /**
   * A deliberately worthless ticket, printed to the SAME pitch as a real one.
   *
   * Same registration, same tail, same cut — the point is to absorb whatever the first ticket
   * absorbs, so it has to be handled identically. A shorter scrap would land its cut somewhere
   * else and prove nothing about where the next one will land.
   */
  /** Dots between the side margins — the width a layout is composed for. */
  get layoutWidthDots(): number {
    const span = this._paperSpan?.widthDots ?? this._headDots ?? PAPER_PRESETS[this._paper]?.width ?? 576;
    return span - 2 * Math.min(this._marginDots, Math.floor(span / 4));
  }

  /**
   * A TICKET COMPOSED IN THE PRINTER'S OWN COMMANDS — see layout.ts. Registers, clears the stock's
   * header and cuts exactly like `printTicket`, so a designed ticket lands where a plain one does.
   */
  async printLayout(ops: LayoutOp[], mode: 'cut' | 'tear' = 'tear', showHeader = true, opts: { accents?: boolean } = {}) {
    const blackMark = mode !== 'cut';
    this.beginTicket();
    if (blackMark) await this.registerAtMark();
    await this.write(C.CMD_INIT);
    const area = this.printAreaCommand();
    if (area) await this.write(area);
    // Header off = pre-printed stock: skip the header slot, exactly as a text ticket does.
    const skip = this._topDots + (showHeader ? 0 : HEADER_SLOT_LINES * LINE_SPACING_DOTS);
    if (skip > 0) await this.feedDotsInternal(skip);
    for (const part of encodeLayout(ops, this.layoutWidthDots, this._qrMethod, opts.accents ?? true)) await this.write(part);
    await this.finishTicket(blackMark);
    this.addLog(`Designed ticket, ${ops.length} lines (${blackMark ? 'registered to the mark/gap' : 'cut by length'})`);
    if (this._stalledDuringJob) throw new Error('The printer stalled on part of this ticket — check it before handing it over.');
  }

  /**
   * THE TYPE SLIP — one ticket that shows, by looking, which of the commands a designed ticket uses
   * this firmware draws: tall type, tab columns, accented letters, rules (picture and underline),
   * plus the serif ROM font and box frames for templates that want them. Same registration and
   * header skip as a real ticket, so it also shows where a ticket's first line lands.
   * ✅ FIELD (EP802 MS532I0, 2026-10-07): every block draws; GS b smoothing and ESC & user
   * characters do not, so they are not on the slip.
   */
  async printTypeSlip(mode: 'cut' | 'tear' = 'tear', showHeader = true) {
    const w = this.layoutWidthDots;
    const label = (n: number, text: string): LayoutOp => ({ k: 'text', text: `${n}  ${text}`, font: 'B', bold: true, lead: 4 });
    const gap: LayoutOp = { k: 'gap', dots: 14 };
    const fw = (str: string) => [...str].flatMap((ch) => (ch === ' ' ? [0xA1, 0xA1] : /\d/.test(ch) ? [0xA3, 0xB0 + +ch] : [0xA3, 0xC1 + ch.charCodeAt(0) - 65]));
    const half = Math.floor(w / 24 / 2);
    const ops: LayoutOp[] = [
      { k: 'text', text: 'TYPE CHECK - NOT A TICKET', bold: true, align: 'C' }, gap,
      label(1, 'TALL TYPE'), { k: 'text', text: 'H3 H4', w: 2, h: 4, bold: true }, gap,
      label(2, 'COLUMNS - SEAT AT THE MIDDLE'), { k: 'text', text: 'ROW\tSEAT', w: 2, h: 2, bold: true, tabs: [half] }, gap,
      label(3, 'ACCENTS'), { k: 'text', text: 'OUÉDRAOGO · ÇA · NUÑEZ', bold: true }, gap,
      label(4, 'RULES - TWO SOLID LINES'), { k: 'rule', dots: 3 }, { k: 'gap', dots: 10 }, { k: 'rule', dots: 3, via: 'underline' }, gap,
      label(5, 'SERIF FONT'), { k: 'raw', heightDots: 54, bytes: C.concat(new Uint8Array([0x1B, 0x33, 54, 0x1C, 0x26]), new Uint8Array(fw('SERIF 2026')), new Uint8Array([0x1C, 0x2E, 0x0A])) }, gap,
      label(6, 'FRAME'), { k: 'raw', heightDots: 72, bytes: C.concat(new Uint8Array([0x1B, 0x74, 0, 0x1B, 0x33, 24, 0xC9]), new Uint8Array(12).fill(0xCD), new Uint8Array([0xBB, 0x0A, 0xBA]), new TextEncoder().encode('   FRAME    '), new Uint8Array([0xBA, 0x0A, 0xC8]), new Uint8Array(12).fill(0xCD), new Uint8Array([0xBC, 0x0A, 0x1B, 0x74, 16])) },
    ];
    await this.printLayout(ops, mode, showHeader);
    this.addLog('Type slip printed');
  }

  async printAlignmentTicket(mode: 'cut' | 'tear' = 'tear') {
    const blackMark = mode !== 'cut';
    this.beginTicket();
    if (blackMark) await this.registerAtMark();
    await this.write(C.CMD_INIT);
    const area = this.printAreaCommand();
    if (area) await this.write(area);
    await this.write(C.cmdSetLineSpacing(LINE_SPACING_DOTS));
    if (this._topDots > 0) await this.feedDotsInternal(this._topDots);

    const { chars } = this.layoutColumns();
    await this.write(C.cmdSetAlignment(1));
    await this.send(C.cmdSetBold(true), C.cmdSetTextSize(2, 2));
    await this.printLine('TEST');
    await this.send(C.cmdSetTextSize(1, 1), C.cmdSetBold(false));
    await this.printLine('-'.repeat(chars));
    await this.printLine('NOT A TICKET — THROW AWAY');
    await this.printLine('Checking the cut lands right');
    await this.printLine('-'.repeat(chars));
    // Pad to roughly a real ticket's height so the tail and the cut behave the same way.
    await this.feed(6);

    await this.finishTicket(blackMark);
    this._settingsDirty = false;
    this.addLog('Alignment test printed — settings applied');
  }

  /** Dots inset on EACH side (8 dots = 1mm). */
  setMargin(dots: number) {
    this._settingsDirty = true;
    this._marginDots = Math.max(0, Math.round(dots));
    this.addLog(`Side margin set to ${this._marginDots} dots (${mm(this._marginDots).toFixed(1)}mm each side)`);
  }
  get marginDots() { return this._marginDots; }

  /**
   * The print area for the current paper and margin. One producer, because it is written in two
   * places (on connect and at the top of every ticket) and they must not disagree — a ticket laid
   * out to a different width than the one the connection set is a ticket that wraps.
   */
  private printAreaCommand(): Uint8Array | null {
    // A MEASURED head beats a preset: the presets assumed a 832-dot head nobody ever measured, and
    // the 58 preset (left 224 + 384 wide) runs off the end of a 576-dot one.
    // The PAPER's measured span on the head wins over the head itself: on a 104 mm head with 80 mm
    // stock, printing the full head width runs text off both edges (✅ FIELD, EP802).
    if (this._paperSpan) {
      const margin = Math.min(this._marginDots, Math.floor(this._paperSpan.widthDots / 4));
      return C.concat(C.cmdSetLeftMargin(this._paperSpan.leftDots + margin), C.cmdSetPrintWidth(this._paperSpan.widthDots - margin * 2));
    }
    if (this._headDots) {
      const margin = Math.min(this._marginDots, Math.floor(this._headDots / 4));
      return C.concat(C.cmdSetLeftMargin(margin), C.cmdSetPrintWidth(this._headDots - margin * 2));
    }
    const preset = PAPER_PRESETS[this._paper];
    if (!preset) return null;
    // Never let the inset eat the page: leave at least half the width to print into.
    const margin = Math.min(this._marginDots, Math.floor(preset.width / 4));
    return C.concat(
      C.cmdSetLeftMargin(preset.leftMargin + margin),
      C.cmdSetPrintWidth(preset.width - margin * 2),
    );
  }

  /** Dots of paper the head advances per line at the spacing `printTicket` sets. */
  private _tailDots = 240;

  /** Trailing feed from the last printed line to the perforation, in dots (8 dots = 1mm). */
  setTailDots(dots: number) {
    this._settingsDirty = true;
    this._tailDots = Math.max(0, Math.round(dots));
    this.addLog(`Tail feed set to ${this._tailDots} dots (${mm(this._tailDots).toFixed(1)}mm)`);
  }
  get tailDots() { return this._tailDots; }

  /** Set by `write` when a stall had to be cleared mid-job — see there for why it matters. */
  private _stalledDuringJob = false;

  private beginTicket() { this._slots = 0; this._stalledDuringJob = false; }

  /** Every paper-advancing call in a ticket goes through here so the invariant is measurable. */
  private countSlots(n: number) { this._slots += n; }

  /**
   * TWO KINDS OF STOCK, AND THIS CODE ONLY HANDLED ONE.
   *
   * Pre-printed ticket stock carries BLACK MARKS — one per ticket, read by a sensor — and the
   * printer can feed to the next mark on command. Plain receipt roll has none, and the only way to
   * land a cut in the right place is to make every ticket the same height and pad the tail.
   *
   * This file had the markless half only. The mark branch was deleted with the note "on markless
   * stock ESC FF moved no paper", which was a true observation about the roll being tested and the
   * wrong conclusion: it removed registration for the stock that DOES have marks. The consequence
   * showed up on ZIFA's pre-printed Zimbabwe stock — nothing registered the paper before printing,
   * so the text landed wherever the roll happened to be sitting, on top of the printed header.
   *
   * tkt-frontend still has both branches and is the reference; this restores them rather than
   * inventing a third behaviour, so the two consoles print identically on identical stock.
   *
   * `mode` is what selects between them, which makes the "Black Mark (Tear)" / "Blade Cut" control
   * mean something again — it has been wired to nothing since the branch was removed.
   */
  private usesBlackMark(t: MasungTicketData) { return t.mode !== 'cut'; }

  /**
   * True once a ticket has ended ON a mark, so the paper is already sitting at the top of the next
   * one. Reset whenever anything else could have moved it.
   */
  private _registeredAtMark = false;

  /**
   * Register the paper at the top of a ticket. Marked stock only — see `finishTicket`.
   *
   * SKIPPED WHEN THE PAPER IS ALREADY THERE, which is the normal case between tickets of one order.
   * `finishTicket` ends by feeding to the next mark and cutting, so the roll is already positioned;
   * feeding to "the next mark" again would advance a whole further ticket and cut it off blank.
   * On a four-ticket order that is four wasted tickets, and on pre-printed stock those cost money.
   * The registration is for the FIRST ticket of a run, where the roll is wherever the last operator
   * left it — which is the case that put print on top of the Zimbabwe header.
   */
  private async registerAtMark() {
    /**
     * TWO THINGS HAPPEN HERE AND ONLY ONE OF THEM IS CONDITIONAL.
     *
     * `cmdSetCommandMode(3)` is what ENABLES THE MARK SENSOR. It used to sit below the early
     * return, so the second print of a session skipped it — and then `CMD_INIT` at the top of
     * printTicket reset the printer, leaving `finishTicket` asking a sensor that was no longer
     * listening to feed to a mark. The first ticket printed; every one after it did nothing at all,
     * not even a cut, which is exactly what was reported from the counter.
     *
     * Setting the mode is a few bytes and idempotent, so it happens every ticket. Only the PAPER
     * MOVEMENT — feed to the mark and cut — is worth skipping, and only because `finishTicket` has
     * already left the roll at the top of the next ticket.
     */
    await this.write(C.CMD_INIT);
    await this.write(C.cmdSetCommandMode(3));   // EPOS mode — enables the mark sensor

    if (this._registeredAtMark) return;
    // A calibrated profile registers the way it was measured: to the CUT position, then cut — so the
    // first ticket starts at the perforation, as every following one will.
    await this.write(this._profiled ? this.markFeedCmd : C.CMD_MARK_FEED_PRINT);
    await this.write(C.cmdCut(true));           // cut there, so this ticket starts clean
    if (this._profiled && this._backFeedDots > 0) await this.write(C.cmdBackFeedDots(this._backFeedDots));
    this._registeredAtMark = true;
  }

  /**
   * A NEW PRINT JOB CANNOT ASSUME WHERE THE PAPER IS.
   *
   * `_registeredAtMark` is true after a ticket ends on a mark, which is right for the NEXT ticket
   * of the same order — they come out back to back with nobody touching the roll. Between jobs a
   * person has been at the machine: they tore the ticket off, pressed Feed, opened the lid. Holding
   * the belief across that gap is how "Print again" would quietly print off-position.
   *
   * Called by the caller that knows a job is starting, because only it can see that boundary.
   */
  beginPrintJob() {
    this._registeredAtMark = false;
  }

  private async finishTicket(blackMark: boolean) {
    // The height invariant only decides the cut on MARKLESS stock. With marks the sensor decides,
    // so a height change there is harmless and the warning would be noise.
    if (!blackMark && this._lastTicketSlots !== null && this._lastTicketSlots !== this._slots) {
      // Not a warning to be tidied away later — this IS the misalignment, caught at its cause.
      this.addLog(
        `⚠ TICKET HEIGHT CHANGED: ${this._lastTicketSlots} → ${this._slots} line slots. ` +
        `The cut will miss the perforation by ~${mm(Math.abs(this._slots - this._lastTicketSlots) * LINE_SPACING_DOTS).toFixed(1)}mm. ` +
        `Every optional row must still occupy its slot.`
      );
    }
    this._lastTicketSlots = this._slots;

    if (blackMark) {
      // Profiled: GS FF (feed to the CUT position — the cut offset the setup measured applies), the
      // vendor's own ticket ending. Unprofiled: unchanged, so a till that works today keeps working.
      await this.write(this._profiled ? this.markFeedCmd : C.CMD_MARK_FEED_PRINT);
      await this.write(C.cmdCut(true));
      if (this._profiled && this._backFeedDots > 0) await this.write(C.cmdBackFeedDots(this._backFeedDots));
      // The roll now sits at the top of the next ticket, so the next one needs no registration.
      this._registeredAtMark = true;
      return;
    }
    /**
     * The tail pays for the top inset, so the PITCH does not change.
     *
     * Markless stock lands its cut purely on total height: top + content + tail. Adding a top
     * margin without taking it back here makes every ticket taller than its pitch, and the cut
     * walks further off the perforation with every ticket printed — the slow failure this whole
     * height invariant exists to prevent.
     */
    const tail = this._tailDots - this._topDots;
    if (tail < 0) {
      this.addLog(
        `⚠ TOP MARGIN EXCEEDS THE TAIL: ${this._topDots} dots of top vs a ${this._tailDots}-dot tail. ` +
        `The ticket is now ${mm(-tail).toFixed(1)}mm longer than its pitch and the cut will drift. ` +
        `Raise the ticket tail or lower the top margin.`
      );
    }
    await this.feedDots(Math.max(0, tail));
    await this.write(C.cmdCut(true));
    this._registeredAtMark = false;
  }

  async printLine(text = '') { this.countSlots(1); await this.write(C.cmdPrintText(text, true)); }
  async printText(text: string) { await this.write(C.cmdPrintText(text, false)); }
  async init() { this.paperMoved(); await this.write(C.CMD_INIT); this.addLog('Init'); }

  // ── Mark sensor commands (SDK 3.30-3.33) ──
  async markPosition() { this.paperMoved(); await this.write(C.CMD_MARK_POS); this.addLog('Mark: detect & stop at mark'); }
  async markFeedPrint() { this.paperMoved(); await this.write(C.CMD_MARK_FEED_PRINT); this.addLog('Mark: feed to print position'); }
  async markFeedCut() { this.paperMoved(); await this.write(C.CMD_MARK_FEED_CUT); this.addLog('Mark: feed to cut position'); }
  async markCut(detect = true) { this.paperMoved(); await this.write(C.cmdMarkCut(detect)); this.addLog(`Mark cut (detect=${detect})`); }
  // Both are NVRAM writes, so both restart the printer — see `writeSetting`.
  async setMarkCutOffset(offset: number) { await this.writeSetting(C.cmdSetMarkCutOffset(offset), `Mark cut offset: ${offset}`); }
  async setMarkPrintOffset(offset: number) { await this.writeSetting(C.cmdSetMarkPrintOffset(offset), `Mark print offset: ${offset}`); }

  async markTest(count = 3) {
    this.paperMoved();
    await this.write(C.CMD_INIT);
    await this.write(C.cmdSetCommandMode(3));
    for (let i = 1; i <= count; i++) {
      this.addLog(`Mark test ${i}/${count}: feeding to mark...`);
      await this.write(C.CMD_MARK_FEED_PRINT);  // Feed to black mark (perforation)
      await this.write(C.cmdCut(true));           // Cut at the mark
      // Print label on next page so user can see where the cut landed
      await this.write(C.cmdSetAlignment(1));
      await this.send(C.cmdSetBold(true), C.cmdSetTextSize(2, 2));
      await this.printLine(`-- ${i} --`);
      await this.send(C.cmdSetTextSize(1, 1), C.cmdSetBold(false));
      await this.printLine('CUT WAS ABOVE THIS LINE');
      await this.feed(3);
      this.addLog(`Mark test ${i}/${count}: cut`);
    }
    this.addLog(`Mark test done (${count} cuts)`);
  }

  async queryAllStatus() {
    const cmds = [C.CMD_STATUS1, C.CMD_STATUS2, C.CMD_STATUS3, C.CMD_STATUS4, C.CMD_STATUS5];
    const parsers = [C.parseStatus1, C.parseStatus2, C.parseStatus3, C.parseStatus4, C.parseStatus5];
    const results: number[] = [];
    for (let i = 0; i < 5; i++) {
      try {
        const r = await this.query(cmds[i], 1, 1000);
        results.push(r.length > 0 ? parsers[i](r[r.length - 1]) : -1);
      } catch { results.push(-1); }
    }
    return { s1: results[0], s2: results[1], s3: results[2], s4: results[3], s5: results[4] };
  }

  async getInfo(type: number) {
    const r = await this.query(C.cmdGetInfo(type), 32, 2000);
    return r.length > 0 ? new TextDecoder().decode(r).replace(/\0/g, '').trim() : '';
  }

  async printTicket(t: MasungTicketData) {
    // The separator is drawn to the INSET width — a fixed 32 dashes would overhang a narrowed area
    // and wrap onto a second line, which silently adds a line slot and moves the cut.
    const blackMark = this.usesBlackMark(t);
    this.beginTicket();
    // REGISTER FIRST, on marked stock. Without this the head starts wherever the roll was left,
    // which on pre-printed stock means printing over the design — reported from ZIFA's counter as
    // text landing on top of the Zimbabwe header.
    if (blackMark) await this.registerAtMark();
    await this.write(C.CMD_INIT);
    // CMD_INIT resets the print area, so it has to be set again here and not only on connect.
    const area = this.printAreaCommand();
    if (area) await this.write(area);
    await this.write(C.cmdSetLineSpacing(LINE_SPACING_DOTS));
    // Clear the pre-printed branding at the top of the ticket before the first line lands on it.
    // Internal feed: this is layout, not the paper getting away from us — see feedDotsInternal.
    if (this._topDots > 0) await this.feedDotsInternal(this._topDots);

    // ── Header (or blank space of equal height) ──
    // 58mm = 16 chars at 2x; 80mm = 24 chars at 2x
    // Follows the inset area, not the paper — see layoutColumns. At 2x, half as many characters fit.
    const { chars, tabCol } = this.layoutColumns();
    const sepLine = '-'.repeat(chars);
    const maxEventLen = Math.floor(chars / 2);
    const showHeader = t.showHeader !== false;
    if (showHeader) {
      await this.write(C.cmdSetAlignment(1));
      await this.send(C.cmdSetBold(false), C.cmdSetTextSize(1, 1));
      await this.printLine('EVENT TICKET');
      await this.feed(1);

      // ── Event Name (large bold) ──
      await this.write(C.cmdSetAlignment(1));
      await this.send(C.cmdSetBold(true), C.cmdSetTextSize(2, 2));
      await this.printLine(up(t.eventName, '').slice(0, maxEventLen));
      await this.send(C.cmdSetTextSize(1, 1), C.cmdSetBold(false));

      // ── Subtitle ──
      // Printed even when empty. A blank line and a missing line look the same to a reader and
      // are 28 dots apart to the cutter; the slot is what keeps the perforation where it is.
      await this.write(C.cmdSetAlignment(1));
      await this.send(C.cmdSetBold(true));
      await this.printLine(up(t.subtitle, ''));
      await this.write(C.cmdSetBold(false));
    } else {
      // Feed blank lines to match header height: "EVENT TICKET" + gap + 2x name (2 lines) +
      // subtitle. The subtitle slot is now unconditional above, so this is too — it used to
      // read `t.subtitle ? 5 : 4`, which made a headerless ticket's height depend on data.
      await this.feed(HEADER_SLOT_LINES - 1);
    }

    // ── Separator ──
    await this.write(C.cmdSetAlignment(1));
    await this.feed(1);
    await this.printLine(sepLine);

    // ── Detail rows – dual column using ESC D tab stops ──
    // 58mm = 32 chars → tab at 16; 80mm = 48 chars → tab at 24
    await this.write(C.cmdSetAlignment(0));
    await this.write(C.cmdSetTabPositions([tabCol]));
    const bold = async (on: boolean) => { await this.write(C.cmdSetBold(on)); };
    const TAB = C.CMD_TAB;

    // Row 1: Date | Kickoff
    await this.printText('Date');
    await this.write(TAB);
    await this.printLine('Kickoff');
    await bold(true);
    await this.printText(up(t.date));
    await this.write(TAB);
    /**
     * PRINTED AS GIVEN. This used to try `new Date(t.time)` and reformat whatever came back, which
     * is a printer guessing at the meaning of its own input — and it guessed in the reader's
     * timezone. The caller knows what these fields mean and now hands over display-ready strings
     * (see scanItemToMasungTicket); a device driver formatting dates is how the raw ISO timestamp
     * reached the paper in the first place.
     */
    await this.printLine(up(t.time));
    await bold(false);

    // Rows 2-4 are UNCONDITIONAL. They were `if (t.section || t.row)` and so on, which is the
    // whole misalignment: a general-admission ticket has no seat, section or row, so it printed
    // six lines fewer than a seated one — ~21mm — and then both got the same trailing feed. An
    // empty value prints as "-", which is also the honest thing to show for "no seat".
    const row = async (leftLabel: string, rightLabel: string, leftVal: Printable, rightVal: Printable) => {
      await this.printText(leftLabel);
      await this.write(TAB);
      await this.printLine(rightLabel);
      await bold(true);
      await this.printText(up(leftVal));
      await this.write(TAB);
      await this.printLine(up(rightVal));
      await bold(false);
    };

    await row('Class', 'Gate', t.ticketType, t.gate);
    await row('Section', 'Row', t.section, t.row);
    await row('Seat', 'Entry', t.seat, t.entryWindow);

    // ── Separator ──
    await this.write(C.cmdSetAlignment(1));
    await this.printLine(sepLine);

    // ── QR Code section ──
    await this.write(C.cmdSetAlignment(1));
    await this.feed(1);
    await this.send(C.cmdSetBold(true));
    await this.printLine('SCAN AT TURNSTILE');
    await this.write(C.cmdSetBold(false));
    await this.feed(1);
    await this.write(C.cmdQr(this._qrMethod, t.qrData, 10));
    await this.feed(1);
    await this.send(C.cmdSetBold(true));
    await this.printLine('VALID ONCE');
    await this.write(C.cmdSetBold(false));
    await this.feed(1);

    // ── Barcode ──
    // Coerced like every other field: a barcode is a string of symbols, and a numeric reference
    // reaching it would have thrown exactly where the Seat row did.
    await this.write(C.cmdBarcode1d(10, up(t.orderId, ''), 2, 50, 0, 2));
    await this.feed(1);

    // ── Footer ──
    await this.write(C.cmdSetAlignment(1));
    await this.printLine('DO NOT FOLD OVER QR CODE');

    // ── Summary strip (redundant seating info) ──
    await this.write(C.cmdSetAlignment(1));
    await this.printLine(sepLine);
    await this.printLine(up(t.eventName, '').slice(0, tabCol * 2));
    await this.send(C.cmdSetBold(true));
    await this.printLine(up(t.ticketType));
    await this.send(C.cmdSetBold(false));
    await this.feed(1);
    await this.write(C.cmdSetAlignment(0));
    await this.write(C.cmdSetTabPositions([tabCol]));
    // > = gate entry, # = section, ~ = row, * = seat
    await this.printText(`> ${up(t.gate)}`);
    await this.write(TAB);
    await this.printLine(`Section ${up(t.section)}`);
    await this.printText(`Row ${up(t.row)}`);
    await this.write(TAB);
    await this.printLine(`Seat ${up(t.seat)}`);
    await this.feed(2);
    await this.write(C.cmdSetAlignment(1));
    await this.write(C.cmdBarcode1d(10, up(t.orderId, ''), 2, 50, 0, 2));
    await this.feed(1);

    // ── End: to the next mark, or pad to the pitch — see finishTicket ──
    await this.finishTicket(blackMark);
    this.addLog(
      `Ticket: ${up(t.attendeeName, '?')} - ${up(t.orderId, '?')} ` +
      `(${blackMark ? 'registered to the mark/gap' : `markless, ${this._slots} slots + ${this._tailDots} dots`})`
    );

    // Reported AFTER the cut, so the paper is in a known place before the caller hears about it.
    if (this._stalledDuringJob) {
      const msg =
        'The printer rejected part of this ticket, so it may be blank or incomplete. ' +
        'Check the paper before handing it over, and print again if it is wrong.';
      this._notifyStatus({ connected: true, error: msg });
      throw new Error(msg);
    }
  }

  // ══════════════════════════════════════════════════════════════════════════════════════════
  // PROFILE — what the setup measured about the unit in front of us. See profile.ts.
  // ══════════════════════════════════════════════════════════════════════════════════════════

  /** `dc3` is what every ticket used before setup existed; kept as the unprofiled default. */
  private _qrMethod: QrMethod = 'dc3';
  private _headDots: number | null = null;
  /** True once a setup profile is in force — selects the measured registration/cut sequence. */
  private _profiled = false;
  /** Reverse after a mark cut so the next ticket prints from its top edge — measured, see printCutSlip. */
  private _backFeedDots = 0;
  private _paperSpan: { leftDots: number; widthDots: number } | null = null;
  /**
   * WHICH COMMAND MAKES THIS FIRMWARE SEARCH FOR THE MARK. ✅ FIELD (EP802 APP22-11-11-A5): `GS FF`
   * (1D 0C) is accepted and IGNORED; `ESC FF` (1B 0C) searches (and raises "mark not found" when it
   * cannot see one); `FF` (0C) does nothing. The SDKs and the vendor ticket ending say GS FF — on
   * this unit that is a silent no-op, which is why "Find a mark" did nothing.
   */
  private _markFeed: 'escff' | 'gsff' = 'escff';
  get markFeedCmd() { return this._markFeed === 'gsff' ? C.CMD_MARK_FEED_CUT : C.CMD_MARK_FEED_PRINT; }
  get qrMethod() { return this._qrMethod; }
  get headDots() { return this._headDots; }
  get profiled() { return this._profiled; }

  applyProfile(p: PrinterProfile | null) {
    this._profiled = !!p?.paperType;
    this._qrMethod = p?.qrMethod ?? 'dc3';
    this._headDots = p?.headDots ?? null;
    this._backFeedDots = p?.backFeedDots ?? 0;
    this._markFeed = p?.markFeed ?? 'escff';
    this._paperSpan = p?.paperLeftDots !== undefined && p?.paperWidthDots ? { leftDots: p.paperLeftDots, widthDots: p.paperWidthDots } : null;
    if (p?.marginDots !== undefined) this._marginDots = p.marginDots;
    if (p?.topDots !== undefined) this._topDots = p.topDots;
    if (p?.tailDots !== undefined) this._tailDots = p.tailDots;
    this._registeredAtMark = false;
    this.addLog(p ? `Profile in force: ${p.key} (paper=${p.paperType ?? '?'}, qr=${this._qrMethod}, head=${this._headDots ?? '?'})` : 'No profile — legacy settings');
  }

  // ══════════════════════════════════════════════════════════════════════════════════════════
  // SETUP PROBES — each returns what the printer actually did or said, never a conclusion.
  // ══════════════════════════════════════════════════════════════════════════════════════════

  /** Raw DLE EOT 1..5 replies; `null` where the printer stayed silent. */
  async readStatusRaw(): Promise<(number | null)[]> { return this.exclusive(async () => {
    const out: (number | null)[] = [];
    for (const n of [1, 2, 3, 4, 5] as const) {
      const r = await this.ask(new Uint8Array([0x10, 0x04, n]), 1, 500);
      out.push(r.length ? r[r.length - 1] : null);
    }
    return out;
  }); }

  /** GS I 1..7 and the Masung type byte, as text. Empty string = no answer. */
  async readReportedIdentity(): Promise<Record<string, string>> { return this.exclusive(async () => {
    const names = ['Head model', 'Board / type', 'Firmware', 'Manufacturer', 'Model', 'Encoding', 'Checksum'];
    const out: Record<string, string> = {};
    for (let n = 1; n <= 7; n++) {
      const r = await this.ask(C.cmdGetInfo(n), n === 7 ? 2 : 32, 600);
      out[names[n - 1]] = n === 7
        ? (r.length ? Array.from(r).map((b) => b.toString(16).padStart(2, '0')).join(' ') : '')
        : new TextDecoder().decode(r).replace(/[\0\x01-\x1f]/g, '').trim();
    }
    const t = await this.ask(C.CMD_PRINTER_TYPE, 4, 600);
    out['Type byte'] = t.length >= 4 && t[0] === 0x12 ? '0x' + t[3].toString(16).padStart(2, '0') : t.length ? `unexpected: ${hexPreview(t)}` : '';
    return out;
  }); }

  /** One raw mark-sensor reading, or `null` if this firmware does not answer `ESC 0xCC 1`. */
  async readSensor(): Promise<number | null> { return this.exclusive(() => this.sensorOnce()); }
  private async sensorOnce(): Promise<number | null> {
    const r = await this.ask(C.CMD_READ_SENSOR, 1, 400);
    return r.length ? r[r.length - 1] : null;
  }

  /**
   * WALK THE PAPER PAST THE SENSOR AND WRITE DOWN WHAT IT SEES.
   *
   * Feeds `stepMm` at a time and reads the sensor after each step. The plot of that is the stock as
   * the printer perceives it: where the marks are, how long they are, how far apart (the pitch), and
   * whether a mark is distinguishable from paper at all. Stops early if the sensor never answers.
   */
  async sensorTrace(lengthMm: number, stepMm: number, onSample?: (s: TraceSample) => void, shouldStop?: () => boolean): Promise<TraceSample[]> { return this.exclusive(async () => {
    this.paperMoved();
    await this.write(C.CMD_INIT);
    // Does this firmware report a raw reading at all? ✅ FIELD: the EP802 does not. Then the status
    // bytes are recorded instead — giving up after 2 mm (what the first version did) proved nothing.
    const reportsAd = (await this.sensorOnce()) !== null;
    if (!reportsAd) this.noteWire('note', 'sensor gives no raw reading (1B CC 01 silent) — tracing status bits instead');
    const samples: TraceSample[] = [];
    for (let at = 0; at <= lengthMm; at += stepMm) {
      if (shouldStop?.()) break;
      let s: TraceSample;
      if (reportsAd) {
        const ad = await this.sensorOnce();
        s = { mm: at, ad: ad ?? NaN };
      } else {
        const s4 = await this.ask(C.CMD_STATUS4, 1, 300);
        const gr = await this.ask(C.CMD_PAPER_SENSOR_STATUS, 1, 300);
        s = { mm: at, ad: NaN, raw: [s4.length ? s4[s4.length - 1] : null, gr.length ? gr[gr.length - 1] : null] };
      }
      samples.push(s); onSample?.(s);
      await this.write(C.cmdFeedDots(Math.round(stepMm * DOTS_PER_MM)));
    }
    await this.write(C.cmdCut(true));
    return samples;
  }); }

  /** Store the paper type — the black-mark switch. Restarts the printer; see `writeSetting`. */
  async setPaperType(t: PaperType) {
    await this.writeSetting(C.cmdSetPaperType(t), `Paper type: ${t}`);
  }

  async setMarkThreshold(n: number) {
    await this.writeSetting(C.cmdSetMarkThreshold(n), `Mark threshold: ${n}`);
  }

  /** GS FF then read status 2+3 — did the printer find a mark? Returns the raw bytes. */
  async findMark(): Promise<{ s2: number | null; s3: number | null }> { return this.exclusive(async () => {
    this.paperMoved();
    await this.write(C.CMD_INIT);
    await this.write(C.cmdSetCommandMode(3));
    await this.write(this.markFeedCmd);
    await this.write(C.cmdCut(true));
    await sleep(400);
    const s2 = await this.ask(C.CMD_STATUS2, 1, 600);
    const s3 = await this.ask(C.CMD_STATUS3, 1, 600);
    return { s2: s2.length ? s2[0] : null, s3: s3.length ? s3[0] : null };
  }); }

  async recover(clearBuffer = true) { await this.write(C.cmdRecover(clearBuffer)); this.addLog('Error recovery sent'); }

  /**
   * THE CAPABILITY SLIP — one piece of paper that answers three questions by looking at it:
   *   - how wide the head really is (the letter at the end of the first ruler line),
   *   - which QR command this firmware prints (block 1 = standard ESC/POS, block 3 = Masung DC3),
   *   - whether it prints pictures at all (block 2).
   * The Masung block goes LAST and is followed by a reset: on ESC/POS firmware it half-parses and
   * would otherwise leave garbage in the buffer for the next job.
   */
  async printCapabilitySlip() {
    this.paperMoved();
    await this.send(C.CMD_INIT, C.CMD_CANCEL_CHINESE, C.cmdSetLeftMargin(0), C.cmdSetPrintWidth(C.HEAD_DOTS));
    await this.send(C.cmdSetAlignment(0), C.cmdSetLineSpacing(30));
    await this.printLine(widthProbeLine());
    await this.printLine('');
    // Column ruler for a head wider than the paper: read the first/last column ON the paper.
    const [tens, units] = edgeProbeLines(Math.floor(C.HEAD_DOTS / 12));
    await this.printLine(tens);
    await this.printLine(units);
    await this.printLine('');
    await this.send(C.cmdSetAlignment(1), C.cmdSetBold(true));
    await this.printLine('TKT PRINTER CHECK');
    await this.write(C.cmdSetBold(false));
    await this.printLine('Not a ticket');
    await this.printLine('');
    await this.printLine('[1] STANDARD QR');
    await this.write(C.cmdQrcodeEscPos('TKT PRINTER TEST 1', 6));
    await this.printLine('');
    await this.printLine('[2] PICTURE');
    const wb = 24, h = 48, bits = new Uint8Array(wb * h);
    for (let y = 0; y < h; y++) for (let x = 0; x < wb; x++) bits[y * wb + x] = (y < 3 || y >= h - 3) ? 0xFF : ((Math.floor(x / 2) % 2) ? 0xFF : 0x00);
    await this.write(C.cmdRaster(wb, h, bits));
    await this.printLine('');
    await this.printLine('[3] MASUNG QR');
    await this.write(C.cmdQrcode('TKT PRINTER TEST 3', 6));
    await this.printLine('');
    await this.send(C.CMD_INIT, C.CMD_CANCEL_CHINESE, C.cmdSetAlignment(1));
    await this.printLine('END OF CHECK');
    await this.feedDotsInternal(160);
    await this.write(C.cmdCut(true));
    this.addLog('Capability slip printed');
  }

  /** Ruler lines from 0 to `lengthMm`, 3 mm apart, at the current print area. Returns the last label. */
  private async printRuler(lengthMm: number): Promise<number> {
    const area = this.printAreaCommand();
    if (area) await this.write(area);
    await this.send(C.cmdSetAlignment(0), C.cmdSetLineSpacing(RULER_STEP_DOTS));
    const { chars } = this.layoutColumns();
    const labels = rulerLabels(lengthMm);
    for (const v of labels) {
      const tag = String(v).padStart(3, ' ');
      await this.printLine(`${tag} ${'-'.repeat(Math.max(4, chars - 8))} ${tag}`);
    }
    return labels[labels.length - 1];
  }

  /**
   * THE BLADE SLIP — a ruler, then a cut RIGHT THERE.
   *
   * The blade sits a fixed distance past the print head, so the cut lands inside the ruler, that
   * distance back from the last line printed. The number at the cut gives it: no tape measure. The
   * remainder is fed out and cut too, so both pieces are in hand.
   */
  async printBladeSlip(lengthMm = 60): Promise<{ lastLabelMm: number }> { return this.exclusive(async () => {
    this.paperMoved();
    await this.send(C.CMD_INIT, C.CMD_CANCEL_CHINESE);
    const last = await this.printRuler(lengthMm);
    await this.write(C.cmdCut(true));
    await this.feedDotsInternal(30 * DOTS_PER_MM);
    await this.write(C.cmdCut(true));
    this.addLog(`Blade slip printed (${lengthMm}mm)`);
    return { lastLabelMm: last };
  }); }

  /**
   * THE CUT SLIP — the cut under test, then a ruler that starts AT that cut.
   *
   * Feed to the mark's cut position and cut: that edge is what the offset controls. Then reverse the
   * paper by the head-to-blade distance so the ruler's 0 is printed on the edge itself (the vendor
   * app reverses 11.25 mm after every cut for the same reason). The number at the perforation is
   * then its distance from the cut. If this firmware does not reverse, 0 lands one blade-distance
   * below the edge — the operator says so and the arithmetic accounts for it.
   */
  async printCutSlip(lengthMm: number, backFeedDots: number): Promise<{ lastLabelMm: number }> { return this.exclusive(async () => {
    this.paperMoved();
    await this.send(C.CMD_INIT, C.CMD_CANCEL_CHINESE, C.cmdSetCommandMode(3));
    await this.write(this.markFeedCmd);
    await this.write(C.cmdCut(true));
    if (backFeedDots > 0) await this.write(C.cmdBackFeedDots(backFeedDots));
    const last = await this.printRuler(lengthMm);
    await this.write(C.cmdCut(true));
    await this.feedDotsInternal(30 * DOTS_PER_MM);
    await this.write(C.cmdCut(true));
    this.addLog(`Cut slip printed (${lengthMm}mm, back-feed ${backFeedDots} dots)`);
    return { lastLabelMm: last };
  }); }

  async fullTest() {
    await this.write(C.CMD_INIT);
    await this.write(C.cmdSetLineSpacing(24));
    await this.send(C.cmdSetAlignment(1), C.cmdSetBold(true), C.cmdSetTextSize(2, 2));
    await this.printLine('MASUNG EP802');
    await this.send(C.cmdSetTextSize(1, 1), C.cmdSetBold(false));
    await this.printLine('SDK Command Audit');
    await this.feed(1);
    await this.write(C.cmdSetAlignment(0));
    await this.printLine('= FORMATTING =');
    await this.send(C.cmdSetBold(true)); await this.printLine('Bold'); await this.write(C.cmdSetBold(false));
    await this.send(C.cmdSetUnderline(1)); await this.printLine('Underline 1'); await this.send(C.cmdSetUnderline(2)); await this.printLine('Underline 2'); await this.write(C.cmdSetUnderline(0));
    await this.send(C.cmdSetInverse(true)); await this.printLine('Inverse!'); await this.write(C.cmdSetInverse(false));
    await this.send(C.cmdSetItalic(true)); await this.printLine('Italic'); await this.write(C.cmdSetItalic(false));
    await this.send(C.cmdSetRotation(true)); await this.printLine('Rotated'); await this.write(C.cmdSetRotation(false));
    await this.printLine('= SIZES =');
    for (let s = 1; s <= 4; s++) { await this.write(C.cmdSetTextSize(s, s)); await this.printLine(`Size ${s}x${s}`); }
    await this.write(C.cmdSetTextSize(1, 1));
    await this.printLine('= BARCODES =');
    await this.printLine('CODE128:'); await this.write(C.cmdBarcode1d(10, 'ABC123', 3, 50, 0, 2)); await this.feed(1);
    await this.printLine('CODE39:'); await this.write(C.cmdBarcode1d(4, 'TEST5678', 3, 50, 0, 2)); await this.feed(1);
    await this.printLine('EAN13:'); await this.write(C.cmdBarcode1d(2, '590123412345', 3, 50, 0, 2)); await this.feed(1);
    await this.printLine('CODE93:'); await this.write(C.cmdBarcode1d(9, 'CODE93', 3, 50, 0, 2)); await this.feed(1);
    await this.printLine('= QR CODE =');
    await this.write(C.cmdQrcode('https://example.com', 4));
    await this.feed(2);
    await this.send(C.cmdSetAlignment(1), C.cmdSetBold(true));
    await this.printLine('Test complete!');
    await this.write(C.cmdSetBold(false));
    await this.feedDots(200);  // ~25mm feed to clear cutter blade
    await this.write(C.cmdCut(true));
    this.addLog('Full test sent');
  }

  async selfTest() { await this.write(C.cmdSelfTest()); this.addLog('Self-test sent'); }

  async sendHex(hex: string) {
    const bytes = new Uint8Array((hex.replace(/\s/g, '').match(/.{1,2}/g) || []).map(b => parseInt(b, 16)));
    await this.write(bytes);
    this.addLog(`Raw hex (${bytes.length} bytes)`);
  }
}

// Singleton instance
export const masungPrinter = new MasungPrinter();
